import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2,
  ChevronLeft,
  ChevronRight,
  FileSpreadsheet,
  GitBranch,
  ListMinus,
  Loader2,
  Pencil,
  Search,
  Trash2,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { cn } from '../../../lib/cn';
import { ConsentBadge } from '../ConsentBadge';
import { CONTACTS_PAGE_SIZE, emailService } from '../../../lib/email/emailService';
import type { EmailContactConListe, EmailLista } from '../../../types/email.types';
import type { EmailAutomation } from '../../../types/automazioni.types';

interface DettaglioListaProps {
  lista: EmailLista;
  /** Le sequenze accese che partono da questa lista. */
  sequenze: EmailAutomation[];
  /** Cambia quando la lista viene modificata da fuori: i contatti si ricaricano. */
  versione: number;
  esportando: boolean;
  onAggiungi: () => void;
  onTogli: (ids: string[]) => Promise<void>;
  onEsporta: () => void;
  onRinomina: () => void;
  onElimina: () => void;
}

/**
 * La lista aperta: quanti sono, chi c'e', e i tasti per aggiungere, togliere,
 * scaricare, rinominare o eliminare.
 */
export function DettaglioLista({
  lista,
  sequenze,
  versione,
  esportando,
  onAggiungi,
  onTogli,
  onEsporta,
  onRinomina,
  onElimina,
}: DettaglioListaProps) {
  const [ricerca, setRicerca] = useState('');
  const [ricercaAttiva, setRicercaAttiva] = useState('');
  const [pagina, setPagina] = useState(0);
  const [membri, setMembri] = useState<EmailContactConListe[]>([]);
  const [totale, setTotale] = useState(0);
  const [caricando, setCaricando] = useState(true);
  const [errore, setErrore] = useState<string | null>(null);
  const [scelti, setScelti] = useState<Set<string>>(new Set());
  const [lavorando, setLavorando] = useState(false);

  // Cambiando lista la pagina rimonta questo riquadro da capo (key = id della
  // lista): ricerca vuota, prima pagina, niente spunte.
  useEffect(() => {
    const t = setTimeout(() => {
      setRicercaAttiva(ricerca);
      setPagina(0);
    }, 350);
    return () => clearTimeout(t);
  }, [ricerca]);

  const filtri = useMemo(
    () => ({ listaId: lista.id, search: ricercaAttiva }),
    [lista.id, ricercaAttiva]
  );

  // Cambiando cosa si guarda, le spunte di prima non valgono piu'.
  useEffect(() => {
    setScelti(new Set());
  }, [filtri, versione]);

  const ultimaRichiesta = useRef(0);

  const carica = useCallback(async () => {
    const questa = ++ultimaRichiesta.current;
    setCaricando(true);
    setErrore(null);
    try {
      const risultato = await emailService.getContacts({ ...filtri, page: pagina });
      if (questa !== ultimaRichiesta.current) return;
      // Togliendo tutti quelli dell'ultima pagina, la pagina non esiste piu':
      // si torna a quella prima invece di mostrare una lista vuota che non lo e'.
      if (risultato.contatti.length === 0 && pagina > 0) {
        setPagina(pagina - 1);
        return;
      }
      setMembri(risultato.contatti);
      setTotale(risultato.totale);
    } catch (e) {
      if (questa === ultimaRichiesta.current) {
        setErrore(e instanceof Error ? e.message : 'Non riesco a leggere i contatti della lista.');
      }
    } finally {
      if (questa === ultimaRichiesta.current) setCaricando(false);
    }
  }, [filtri, pagina]);

  useEffect(() => {
    carica();
  }, [carica, versione]);

  const alterna = (id: string) =>
    setScelti((prima) => {
      const dopo = new Set(prima);
      if (dopo.has(id)) dopo.delete(id);
      else dopo.add(id);
      return dopo;
    });

  const tuttaLaPagina = membri.length > 0 && membri.every((c) => scelti.has(c.id));
  const alternaPagina = () =>
    setScelti((prima) => {
      const dopo = new Set(prima);
      if (tuttaLaPagina) membri.forEach((c) => dopo.delete(c.id));
      else membri.forEach((c) => dopo.add(c.id));
      return dopo;
    });

  const prendiTutti = async () => {
    setLavorando(true);
    try {
      setScelti(new Set(await emailService.idsCorrispondenti(filtri)));
    } catch (e) {
      setErrore(e instanceof Error ? e.message : 'Non riesco a selezionarli tutti.');
    } finally {
      setLavorando(false);
    }
  };

  const togli = async (ids: string[]) => {
    setLavorando(true);
    try {
      await onTogli(ids);
    } finally {
      setLavorando(false);
    }
  };

  const ultimaPagina = Math.max(0, Math.ceil(totale / CONTACTS_PAGE_SIZE) - 1);
  const vuota = lista.contatti === 0;

  return (
    <Card className="border-gray-200 min-w-0">
      <CardContent className="p-4 md:p-5 space-y-4">
        {/* Intestazione della lista */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 break-words">{lista.name}</h2>
            {lista.description && <p className="text-xs text-neutral-500 mt-0.5">{lista.description}</p>}
            <p className="text-xs text-neutral-600 mt-1.5">
              {lista.contatti} {lista.contatti === 1 ? 'contatto' : 'contatti'}, di cui{' '}
              <span className="font-medium text-green-700">{lista.possono_ricevere} possono ricevere</span>
            </p>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <Button variant="outline" size="sm" onClick={onRinomina} title="Cambia nome o descrizione">
              <Pencil className="w-3.5 h-3.5 sm:mr-1.5" />
              <span className="hidden sm:inline">Rinomina</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onElimina}
              title="Elimina la lista: i contatti restano in rubrica"
              className="text-red-600 border-red-200 hover:bg-red-50"
            >
              <Trash2 className="w-3.5 h-3.5 sm:mr-1.5" />
              <span className="hidden sm:inline">Elimina</span>
            </Button>
          </div>
        </div>

        {sequenze.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 text-xs text-neutral-600">
            <GitBranch className="w-3.5 h-3.5 text-primary-500" />
            Chi entra in questa lista parte con:
            {sequenze.map((s) => (
              <Link
                key={s.id}
                to={`/mailing/sequenze/${s.id}`}
                className="px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 border border-primary-100 hover:bg-primary-100"
              >
                {s.name}
              </Link>
            ))}
          </div>
        )}

        {/* Le azioni principali */}
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button onClick={onAggiungi} className="h-10 bg-primary-600 hover:bg-primary-700 text-white">
            <UserPlus className="w-4 h-4 mr-2" />
            Aggiungi contatti
          </Button>
          <Button
            variant="outline"
            onClick={onEsporta}
            disabled={esportando || vuota}
            title="Un foglio Excel con tutti i dati di ogni contatto della lista: rubrica, scheda cliente e modulo compilato"
            className="h-10 text-green-700 border-green-200 hover:bg-green-50"
          >
            {esportando
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <FileSpreadsheet className="w-4 h-4 mr-2" />}
            {esportando ? 'Preparo...' : 'Esporta Excel'}
          </Button>
        </div>

        {/* Chi c'e' dentro */}
        {vuota ? (
          <div className="text-center py-12 rounded-xl border border-dashed border-gray-200">
            <Users className="w-10 h-10 text-neutral-400 mx-auto mb-3 opacity-30" />
            <p className="text-sm text-neutral-600 font-medium">La lista e' vuota</p>
            <p className="text-xs text-neutral-400 mt-1 max-w-sm mx-auto">
              Premi <strong>Aggiungi contatti</strong>, cerca nella rubrica e spunta chi metterci.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <Input
                value={ricerca}
                onChange={(e) => setRicerca(e.target.value)}
                placeholder="Cerca in questa lista…"
                className="pl-9 h-10"
              />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 min-h-9">
              <label className="flex items-center gap-2 text-xs font-medium text-neutral-500 uppercase tracking-wider cursor-pointer select-none">
                {membri.length > 0 && (
                  <input
                    type="checkbox"
                    checked={tuttaLaPagina}
                    onChange={alternaPagina}
                    className="w-4 h-4 rounded border-gray-300 accent-primary-600"
                    aria-label="Spunta tutti quelli in questa pagina"
                  />
                )}
                {totale} {totale === 1 ? 'contatto' : 'contatti'}
              </label>

              {ultimaPagina > 0 && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" disabled={pagina === 0} onClick={() => setPagina((p) => Math.max(0, p - 1))}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-xs text-neutral-500">
                    {pagina + 1} / {ultimaPagina + 1}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagina >= ultimaPagina}
                    onClick={() => setPagina((p) => Math.min(ultimaPagina, p + 1))}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>

            {/* Le spunte restano cambiando pagina: si tolgono tutte insieme. */}
            {scelti.size > 0 && (
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-primary-200 bg-primary-50/50 px-3 py-2">
                <span className="text-xs font-semibold text-gray-900">
                  {scelti.size} {scelti.size === 1 ? 'spuntato' : 'spuntati'}
                </span>
                {scelti.size < totale && (
                  <button
                    type="button"
                    onClick={prendiTutti}
                    disabled={lavorando}
                    className="text-xs text-primary-700 underline underline-offset-2 hover:text-primary-800"
                  >
                    Spunta tutti i {totale}
                  </button>
                )}
                <div className="flex items-center gap-2 ml-auto">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => togli([...scelti])}
                    disabled={lavorando}
                    className="text-red-600 border-red-200 hover:bg-red-50 bg-white"
                  >
                    {lavorando
                      ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      : <ListMinus className="w-3.5 h-3.5 mr-1.5" />}
                    Togli dalla lista
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setScelti(new Set())} disabled={lavorando}>
                    Annulla
                  </Button>
                </div>
              </div>
            )}

            {errore && <p className="text-xs text-red-600">{errore}</p>}

            {caricando ? (
              <div className="flex justify-center py-10">
                <Loader2 className="w-5 h-5 animate-spin text-neutral-400" />
              </div>
            ) : membri.length === 0 ? (
              <p className="text-sm text-neutral-500 text-center py-10">Nessun contatto per questa ricerca</p>
            ) : (
              <div className="space-y-1.5">
                {membri.map((c) => (
                  <div
                    key={c.id}
                    className={cn(
                      'flex items-start gap-3 rounded-lg border px-3 py-2.5',
                      scelti.has(c.id) ? 'border-primary-300 bg-primary-50/50' : 'border-gray-200 bg-white'
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={scelti.has(c.id)}
                      onChange={() => alterna(c.id)}
                      className="mt-0.5 w-4 h-4 rounded border-gray-300 accent-primary-600 flex-shrink-0"
                      aria-label={`Spunta ${c.email}`}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 break-all">{c.email}</span>
                        <ConsentBadge status={c.status} />
                      </div>
                      {(c.full_name || c.company_name) && (
                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-neutral-500">
                          {c.full_name && <span>{c.full_name}</span>}
                          {c.company_name && (
                            <span className="inline-flex items-center gap-1 min-w-0">
                              <Building2 className="w-3 h-3 flex-shrink-0 text-neutral-400" />
                              <span className="truncate max-w-[220px]">{c.company_name}</span>
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => togli([c.id])}
                      disabled={lavorando}
                      title="Togli dalla lista (resta in rubrica)"
                      aria-label={`Togli ${c.email} dalla lista`}
                      className="p-1 rounded-md text-neutral-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-50 flex-shrink-0"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
